import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Local Images
const HOTEL_IMAGE = "/images/pendry-hotel.jpg";
const HOTEL_IMAGE_SRC_SET = "/images/pendry-hotel-800.webp 800w, /images/pendry-hotel-1400.webp 1400w";
const LIBRARY_INTERIOR = "/images/peabody-interior.jpg";
const LIBRARY_INTERIOR_SRC_SET = "/images/peabody-interior-1200.webp 1200w, /images/peabody-interior-2400.webp 2400w";
const PENDRY_ROOM_BLOCK_URL = "https://book.passkey.com/e/51297204";
const CARTO_BASEMAP_KEY = process.env.REACT_APP_CARTO_BASEMAP_KEY
  || 'cb1_3hmr_1_39748e60367d4e52e12aba25';

const FEATURED_LOCATIONS = {
  peabodyLibrary: {
    id: "peabodyLibrary",
    name: "George Peabody Library",
    coords: [39.2970, -76.6155],
    address: "17 E Mt Vernon Pl, Baltimore, MD 21202",
    type: 'venue'
  },
  sagamorePendry: {
    id: "sagamorePendry",
    name: "Sagamore Pendry Baltimore",
    coords: [39.2811103, -76.5919083],
    address: "1715 Thames St, Baltimore, MD 21231",
    type: 'couple'
  },
  barcocina: {
    id: "barcocina",
    name: "Barcocina",
    coords: [39.2811123, -76.5944704],
    address: "1629 Thames St, Baltimore, MD 21231",
    type: 'welcome'
  }
};

const RECOMMENDED_HOTELS = [
  {
    id: "fourSeasons",
    name: "Four Seasons Hotel Baltimore",
    coords: [39.2824461, -76.6021078],
    address: "200 International Dr, Baltimore, MD 21202",
    location: "Fells Point",
    distance: "~1.5 mi",
    url: "https://www.fourseasons.com/baltimore/",
  },
  {
    id: "canopyHilton",
    name: "Canopy by Hilton Harbor Point",
    coords: [39.2793143, -76.5978610],
    address: "1215 Wills St, Baltimore, MD 21231",
    location: "Fells Point",
    distance: "~2 mi",
    url: "https://www.hilton.com/en/hotels/bwihppy-canopy-baltimore-harbor-point/",
  },
  {
    id: "hendersonsWharf",
    name: "Inn at Henderson's Wharf",
    coords: [39.2806721, -76.5895912],
    address: "1000 Fell St, Baltimore, MD 21231",
    location: "Fells Point",
    distance: "~2 mi",
    url: "https://www.hendersonswharf.com/",
  },
  {
    id: "hyattRegency",
    name: "Hyatt Regency Inner Harbor",
    coords: [39.2849983, -76.6134915],
    address: "300 Light St, Baltimore, MD 21202",
    location: "Inner Harbor",
    distance: "~1 mi",
    url: "https://www.hyatt.com/hyatt-regency/en-US/bwirb-hyatt-regency-baltimore-inner-harbor",
  },
  {
    id: "hamptonInn",
    name: "Hampton Inn & Suites Inner Harbor",
    coords: [39.2888311, -76.6127872],
    address: "131 E Redwood St, Baltimore, MD 21202",
    location: "Inner Harbor",
    distance: "~1 mi",
    url: "https://www.hilton.com/en/hotels/balihhx-hampton-suites-baltimore-inner-harbor/",
  },
  {
    id: "royalSonesta",
    name: "Royal Sonesta Harbor Court",
    coords: [39.2828094, -76.6132235],
    address: "550 Light St, Baltimore, MD 21202",
    location: "Inner Harbor",
    distance: "~1 mi",
    url: "https://www.sonesta.com/royal-sonesta/md/baltimore/royal-sonesta-harbor-court-baltimore",
  },
  {
    id: "ivyHotel",
    name: "The Ivy Hotel",
    coords: [39.3035211, -76.6125223],
    address: "205 E Biddle St, Baltimore, MD 21202",
    location: "Mount Vernon",
    distance: "~0.3 mi",
    url: "https://theivybaltimore.com/",
  },
  {
    id: "hotelIndigo",
    name: "Hotel Indigo Downtown",
    coords: [39.2951358, -76.6159096],
    address: "24 W Franklin St, Baltimore, MD 21201",
    location: "Mount Vernon",
    distance: "~0.5 mi",
    url: "https://www.ihg.com/hotelindigo/hotels/us/en/baltimore/balhn/hoteldetail",
  },
  {
    id: "ulysses",
    name: "Ulysses",
    coords: [39.2997310, -76.6157482],
    address: "2 E Read St, Baltimore, MD 21202",
    location: "Mount Vernon",
    distance: "~0.2 mi",
    url: "https://www.hotelulysses.com/",
  }
].map((hotel, index) => ({
  ...hotel,
  number: index + 1,
  type: 'hotel'
}));

const HOTEL_NEIGHBORHOODS = [
  {
    id: 'mountVernon',
    name: 'Mount Vernon',
    // Keep the marker tied to the neighborhood; the pill is offset visually below.
    coords: [39.2996, -76.6149],
    hotelIds: ['ivyHotel', 'hotelIndigo', 'ulysses'],
  },
  {
    id: 'innerHarbor',
    name: 'Inner Harbor',
    coords: [39.2847, -76.6152],
    hotelIds: ['hyattRegency', 'hamptonInn', 'royalSonesta'],
  },
  {
    id: 'fellsPoint',
    name: 'Fells Point',
    coords: [39.2873, -76.5974],
    hotelIds: ['fourSeasons', 'canopyHilton', 'hendersonsWharf'],
  },
].map((neighborhood) => ({
  ...neighborhood,
  type: 'neighborhood',
}));

// This framing-only point keeps the overview centered on the waterfront without
// changing the geographic positions used by any visible marker.
const OVERVIEW_FRAME_POINTS = [
  [39.2752, -76.5965],
];

const SPECIAL_MARKER_DRAWINGS = {
  venue: `
    <svg viewBox="0 0 48 48" width="34" height="34" aria-hidden="true">
      <circle cx="18.5" cy="28" r="9" fill="none" stroke="#333B29" stroke-width="2.2"/>
      <circle cx="29.5" cy="28" r="9" fill="none" stroke="#B78569" stroke-width="2.2"/>
      <path d="m29.5 18.8-4-5.3 4-4.2 4 4.2-4 5.3Z" fill="none" stroke="#333B29" stroke-width="1.8" stroke-linejoin="round"/>
      <path d="M25.5 13.5h8" fill="none" stroke="#333B29" stroke-width="1.5" stroke-linecap="round"/>
    </svg>
  `,
  couple: `
    <svg viewBox="0 0 48 48" width="32" height="32" aria-hidden="true">
      <path d="M9 35h30M12 31h24M14 20h20v11H14zM12 20l12-7 12 7" fill="none" stroke="#333B29" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M18 24v7m6-7v7m6-7v7M8 39c4-2 8-2 12 0s8 2 12 0 7-2 9 0" fill="none" stroke="#69745A" stroke-width="1.6" stroke-linecap="round"/>
    </svg>
  `,
  welcome: `
    <svg viewBox="0 0 48 48" width="31" height="31" aria-hidden="true">
      <path d="M11 15h26c-.8 7.4-5.5 11.7-13 11.7S11.8 22.4 11 15Z" fill="none" stroke="#333B29" stroke-width="1.9" stroke-linejoin="round"/>
      <path d="M24 26.7V36M17.5 36h13" fill="none" stroke="#333B29" stroke-width="1.9" stroke-linecap="round"/>
      <circle cx="18" cy="11" r="1.6" fill="#B78569"/>
      <circle cx="25" cy="8.5" r="1.2" fill="#B78569"/>
      <circle cx="31.5" cy="11.5" r="1.4" fill="#B78569"/>
    </svg>
  `
};

// Custom marker icons by type
const createMarker = ({ id, type, number, name, active = false }) => {
  if (type === 'neighborhood') {
    return L.divIcon({
      className: 'custom-marker custom-marker--neighborhood',
      html: `
        <div
          aria-hidden="true"
          class="neighborhood-pill neighborhood-pill--${id}"
          style="
          display: flex;
          min-width: 148px;
          min-height: 46px;
          align-items: center;
          justify-content: center;
          padding: 8px 13px;
          color: #333B29;
          background: rgba(247, 243, 235, 0.96);
          border: 2px solid #879072;
          border-radius: 999px;
          box-shadow: 0 4px 14px rgba(36, 42, 30, 0.2);
          white-space: nowrap;
        ">
          <span style="
            font-family: 'EB Garamond Variable', Georgia, serif;
            font-size: 12px;
            font-weight: 700;
            line-height: 1;
          ">${name}</span>
        </div>
      `,
      iconSize: [148, 46],
      iconAnchor: [74, 23],
      popupAnchor: [0, -24]
    });
  }

  if (SPECIAL_MARKER_DRAWINGS[type]) {
    const size = type === 'venue' ? 52 : type === 'welcome' ? 44 : 48;
    const outer = type === 'venue'
      ? '#333B29'
      : type === 'welcome'
        ? '#B78569'
        : '#69745A';
    const inner = type === 'venue'
      ? '#F0D8C9'
      : type === 'welcome'
        ? '#F7F3EB'
        : '#E8DFD1';

    return L.divIcon({
      className: `custom-marker custom-marker--${type}`,
      html: `
        <div aria-hidden="true" style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
        ">
          <span style="
            position: absolute;
            left: 50%;
            bottom: 1px;
            width: 12px;
            height: 12px;
            background: ${outer};
            border: 2px solid #2F3624;
            transform: translateX(-50%) rotate(45deg);
          "></span>
          <span style="
            position: relative;
            z-index: 1;
            display: grid;
            width: ${size - 4}px;
            height: ${size - 4}px;
            place-items: center;
            background: ${inner};
            border: 3px solid ${outer};
            border-radius: 50%;
            box-shadow: 0 4px 12px rgba(36, 42, 30, 0.28);
          ">${SPECIAL_MARKER_DRAWINGS[type]}</span>
        </div>
      `,
      iconSize: [size, size],
      iconAnchor: [size / 2, size],
      popupAnchor: [0, -size]
    });
  }

  const configs = {
    hotel: { size: active ? 34 : 28, outer: active ? '#B78569' : '#879072', inner: '#F7F3EB', label: number, animate: false }
  };
  
  const config = configs[type] || configs.hotel;
  const { size, outer, inner, label, animate } = config;
  
  return L.divIcon({
    className: `custom-marker custom-marker--${type}${active ? ' is-active' : ''}`,
    html: `
      <div aria-hidden="true" style="
        width: ${size}px;
        height: ${size}px;
        background: ${outer};
        border: 2px solid #2F3624;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ${animate ? 'animation: pulse 2s infinite;' : ''}
      ">
        <div style="
          width: ${size - 8}px;
          height: ${size - 8}px;
          background: ${inner};
          border-radius: 50%;
          margin: 2px;
          transform: rotate(45deg);
          color: #333B29;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: Arial, sans-serif;
          font-size: ${type === 'hotel' ? 12 : 11}px;
          font-weight: 700;
          line-height: 1;
        ">${label}</div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size]
  });
};

const fitOverviewMap = (map, bounds, { animate = false } = {}) => {
  const isPhone = map.getSize().x < 640;

  map.fitBounds(bounds, {
    paddingTopLeft: isPhone ? [28, 72] : [40, 40],
    paddingBottomRight: isPhone ? [28, 46] : [40, 52],
    maxZoom: 14.5,
    animate: false,
  });

  const targetCenter = map.getCenter();
  const targetZoom = Math.min(map.getZoom(), 14.5);
  map.setView(targetCenter, targetZoom, { animate });
};

// Map Component
const VenueMap = ({ selectedHotelId, onSelectHotel }) => {
  const [activeNeighborhoodId, setActiveNeighborhoodId] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRefs = useRef({});
  const defaultBoundsRef = useRef(null);
  const focusedNeighborhoodRef = useRef(null);
  const onSelectHotelRef = useRef(onSelectHotel);

  useEffect(() => {
    onSelectHotelRef.current = onSelectHotel;
  }, [onSelectHotel]);

  useEffect(() => {
    if (mapInstanceRef.current) return;

    // Frame only the markers visible in the calm overview.
    const overviewLocations = [
      ...Object.values(FEATURED_LOCATIONS),
      ...HOTEL_NEIGHBORHOODS,
    ];
    const bounds = L.latLngBounds(
      [
        ...overviewLocations.map((location) => location.coords),
        ...OVERVIEW_FRAME_POINTS,
      ],
    );
    defaultBoundsRef.current = bounds;

    // Initialize map - will fit bounds after adding markers
    const map = L.map(mapRef.current, {
      center: FEATURED_LOCATIONS.peabodyLibrary.coords,
      zoom: 15, // Higher default zoom for street names
      zoomSnap: 0.5,
      zoomDelta: 0.5,
      scrollWheelZoom: false, // Disabled - use +/- buttons or pinch
      attributionControl: false
    });

    // Keep the overview wide enough to show the harbor south of Fells Point.
    fitOverviewMap(map, bounds);

    L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(CARTO_BASEMAP_KEY)}`, {
      maxZoom: 19,
      subdomains: 'abcd',
    }).addTo(map);

    // Add attribution manually
    L.control.attribution({
      position: 'topright',
      prefix: ''
    }).addAttribution('© <a href="https://carto.com/attributions">CARTO</a> · © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>').addTo(map);

    const addVisibleMarker = (location) => {
      const marker = L.marker(location.coords, {
        icon: createMarker(location),
        keyboard: true,
        title: location.name,
        alt: location.name,
        riseOnHover: true,
        zIndexOffset: location.type === 'neighborhood' ? 200 : 1000
      }).addTo(map);

      const typeLabels = {
        venue: '<p style="font-size: 11px; color: #69745A; margin-top: 6px; font-weight: 600;">WEDDING VENUE</p>',
        couple: '<p style="font-size: 11px; color: #69745A; margin-top: 6px; font-weight: 600;">WHERE WE’RE STAYING</p>',
        welcome: '<p style="font-size: 11px; color: #69745A; margin-top: 6px; font-weight: 600;">FRIDAY WELCOME RECEPTION</p>',
      };

      marker.bindPopup(`
        <div style="font-family: 'EB Garamond Variable', Georgia, serif; min-width: 180px;">
          <strong style="font-size: 15px; color: #333B29;">${location.name}</strong>
          <p style="font-size: 13px; color: #69745A; margin-top: 4px;">${location.address}</p>
          ${typeLabels[location.type] || ''}
        </div>
      `);

      markerRefs.current[location.id] = marker;

      const markerElement = marker.getElement();
      if (markerElement) {
        markerElement.setAttribute('aria-label', location.name);
        markerElement.setAttribute('title', location.name);
      }
    };

    Object.values(FEATURED_LOCATIONS).forEach(addVisibleMarker);

    HOTEL_NEIGHBORHOODS.forEach((neighborhood) => {
      const markerLabel = `${neighborhood.name} hotel neighborhood`;
      const marker = L.marker(neighborhood.coords, {
        icon: createMarker(neighborhood),
        keyboard: true,
        title: markerLabel,
        alt: markerLabel,
        riseOnHover: true,
        zIndexOffset: 200,
      }).addTo(map);

      marker.on('click', () => {
        setActiveNeighborhoodId(neighborhood.id);
        onSelectHotelRef.current(null, false);
      });

      markerRefs.current[`neighborhood:${neighborhood.id}`] = marker;

      const markerElement = marker.getElement();
      if (markerElement) {
        markerElement.setAttribute('aria-label', markerLabel);
        markerElement.setAttribute('title', markerLabel);
      }
    });

    RECOMMENDED_HOTELS.forEach((hotel) => {
      const marker = L.marker(hotel.coords, {
        icon: createMarker(hotel),
        keyboard: true,
        title: hotel.name,
        alt: hotel.name,
        riseOnHover: true,
        zIndexOffset: 2000,
      });

      marker.bindPopup(`
        <a
          href="${hotel.url}"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Visit ${hotel.name} website"
          style="display: block; min-width: 180px; color: inherit; font-family: 'EB Garamond Variable', Georgia, serif; text-decoration: none;"
        >
          <strong style="font-size: 15px; color: #333B29;">${hotel.name}</strong>
          <p style="font-size: 13px; color: #69745A; margin-top: 4px;">${hotel.address}</p>
          <span style="display: inline-block; margin-top: 8px; color: #69745A; font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;">Visit hotel website →</span>
        </a>
      `);
      marker.on('click', () => onSelectHotelRef.current(hotel.id, false));
      markerRefs.current[hotel.id] = marker;
    });

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRefs.current = {};
      }
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const activeNeighborhood = HOTEL_NEIGHBORHOODS.find(
      (neighborhood) => neighborhood.id === activeNeighborhoodId,
    );

    // Selecting an individual hotel always changes from the neighborhood
    // overview to a close, single-hotel view.
    if (activeNeighborhood && selectedHotelId) {
      setActiveNeighborhoodId(null);
      return;
    }

    const selectedNeighborhood = selectedHotelId
      ? HOTEL_NEIGHBORHOODS.find((neighborhood) =>
          neighborhood.hotelIds.includes(selectedHotelId),
        )
      : null;

    HOTEL_NEIGHBORHOODS.forEach((neighborhood) => {
      const marker = markerRefs.current[`neighborhood:${neighborhood.id}`];
      if (!marker) return;

      const shouldHide =
        neighborhood.id === activeNeighborhood?.id ||
        (!activeNeighborhood && neighborhood.id === selectedNeighborhood?.id);

      if (shouldHide && map.hasLayer(marker)) {
        map.removeLayer(marker);
      } else if (!shouldHide && !map.hasLayer(marker)) {
        marker.addTo(map);

        const markerElement = marker.getElement();
        if (markerElement) {
          const markerLabel = `${neighborhood.name} hotel neighborhood`;
          markerElement.setAttribute('aria-label', markerLabel);
          markerElement.setAttribute('title', markerLabel);
        }
      }
    });

    RECOMMENDED_HOTELS.forEach((hotel) => {
      const marker = markerRefs.current[hotel.id];
      if (!marker) return;

      const isSelected = hotel.id === selectedHotelId;
      const isInActiveNeighborhood =
        activeNeighborhood?.hotelIds.includes(hotel.id) || false;
      const shouldShow = isInActiveNeighborhood || isSelected;

      if (shouldShow) {
        marker.setIcon(createMarker({ ...hotel, active: isSelected }));
        marker.setZIndexOffset(isSelected ? 2000 : 300);
        if (!map.hasLayer(marker)) marker.addTo(map);

        const markerElement = marker.getElement();
        if (markerElement) {
          markerElement.setAttribute('aria-label', hotel.name);
          markerElement.setAttribute('title', hotel.name);
        }
      } else if (map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    });

    if (activeNeighborhood) {
      if (focusedNeighborhoodRef.current !== activeNeighborhood.id) {
        map.closePopup();
        const neighborhoodHotelCoords = activeNeighborhood.hotelIds
          .map((hotelId) =>
            RECOMMENDED_HOTELS.find((hotel) => hotel.id === hotelId)?.coords,
          )
          .filter(Boolean);
        map.fitBounds(L.latLngBounds(neighborhoodHotelCoords), {
          padding: [70, 70],
          maxZoom: 16,
          animate: true,
        });
        focusedNeighborhoodRef.current = activeNeighborhood.id;
      }

      if (selectedHotelId) {
        markerRefs.current[selectedHotelId]?.openPopup();
      }
      return;
    }

    focusedNeighborhoodRef.current = null;

    const selectedHotel = RECOMMENDED_HOTELS.find((hotel) => hotel.id === selectedHotelId);
    const selectedMarker = markerRefs.current[selectedHotelId];
    if (selectedHotel && selectedMarker) {
      map.once('moveend', () => selectedMarker.openPopup());
      map.flyTo(selectedHotel.coords, 16, {
        animate: true,
        duration: 0.55,
      });
      return;
    }

    if (defaultBoundsRef.current) {
      map.closePopup();
      fitOverviewMap(map, defaultBoundsRef.current, { animate: true });
    }
  }, [activeNeighborhoodId, selectedHotelId]);

  const showAllNeighborhoods = () => {
    setActiveNeighborhoodId(null);
    onSelectHotelRef.current(null, false);
  };

  return (
    <div className="relative isolate h-[360px] w-full overflow-hidden rounded-sm sm:h-[400px] md:h-[460px] lg:h-[500px]">
      {/* Neutral map treatment with enough contrast for street and water detail. */}
      <style>{`
        .leaflet-tile-pane {
          filter: saturate(82%) brightness(98%) contrast(116%);
        }
        .leaflet-popup-content-wrapper {
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(51, 59, 41, 0.15);
        }
        .leaflet-popup-tip {
          background: white;
        }
        .custom-marker {
          background: transparent;
          border: 0;
          transition: filter 180ms ease;
        }
        .custom-marker.is-active {
          filter: drop-shadow(0 0 7px rgba(183, 133, 105, 0.85));
          z-index: 1000 !important;
        }
        .custom-marker--neighborhood {
          transition: filter 180ms ease, transform 180ms ease;
        }
        .neighborhood-pill {
          transition: transform 180ms ease;
          transform-origin: center;
        }
        .neighborhood-pill--mountVernon {
          transform: translateX(-112px);
        }
        .custom-marker--neighborhood:hover {
          filter: drop-shadow(0 4px 8px rgba(51, 59, 41, 0.18));
        }
        .custom-marker--couple > div {
          transform: translate(12px, -8px);
          transform-origin: 50% 100%;
        }
        @media (max-width: 639px) {
          .neighborhood-pill {
            transform: scale(0.76);
          }
          .neighborhood-pill--mountVernon {
            transform: translate(0, -50px) scale(0.76);
          }
          .neighborhood-pill--fellsPoint {
            transform: translate(0, -28px) scale(0.76);
          }
          .custom-marker--couple > div {
            transform: translate(14px, -8px) scale(0.8);
            transform-origin: 50% 100%;
          }
          .custom-marker--welcome > div {
            transform: translate(-12px, 10px) scale(0.8);
            transform-origin: 50% 100%;
          }
          .custom-marker--venue > div {
            transform: scale(0.84);
            transform-origin: 50% 100%;
          }
        }
        .leaflet-marker-icon:focus-visible {
          outline: 3px solid #B78569;
          outline-offset: 4px;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: rotate(-45deg) scale(1); }
          50% { opacity: 0.8; transform: rotate(-45deg) scale(1.05); }
        }
      `}</style>
      <div 
        ref={mapRef} 
        className="w-full h-full"
        data-testid="venue-map"
      />
      {(activeNeighborhoodId || selectedHotelId) && (
        <button
          type="button"
          onClick={showAllNeighborhoods}
          data-testid="show-all-neighborhoods"
          className="absolute right-3 top-3 z-[500] inline-flex items-center gap-2 rounded-full border border-forest/25 bg-ivory/95 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.11em] text-forest shadow-md backdrop-blur-sm transition-colors hover:border-rust hover:text-rust"
        >
          <span aria-hidden="true">←</span>
          Show all neighborhoods
        </button>
      )}
    </div>
  );
};

export default function TravelPage() {
  const [selectedHotelId, setSelectedHotelId] = useState(null);

  const handleSelectHotel = (hotelId, shouldScroll = true) => {
    setSelectedHotelId(hotelId);

    if (shouldScroll) {
      window.setTimeout(() => {
        document.getElementById('hotel-map')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 50);
    }
  };

  return (
    <div data-testid="travel-page">
      <header className="page-hero">
        <div className="page-hero__inner">
          <div>
            <p className="eyebrow text-olive mb-4">Getting there</p>
            <h1 className="page-title">Travel</h1>
          </div>
          <p className="max-w-md text-xl leading-snug text-forest/75 md:text-right">
            Hotels, airports, train stations, and the places we&rsquo;ll be.
          </p>
        </div>
      </header>

      {/* TOP: Interactive Map Section - "Explore the Area" */}
      <section id="hotel-map" className="py-12 md:py-16 bg-linen">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="eyebrow text-olive mb-4">Explore the area</p>
            <h2 className="font-giaza text-4xl md:text-5xl text-forest">Wedding Venue & Hotels</h2>
            <p className="text-lg text-forest/70 mt-4 max-w-2xl mx-auto">
              The rings mark Saturday&rsquo;s wedding at the George Peabody Library,
              the cocktail marks Friday&rsquo;s welcome reception at Barcocina, and
              the sage marker shows where we&rsquo;ll be staying. Hotels are grouped
              by neighborhood. Select a neighborhood to see its hotels, or choose
              any hotel below to show its exact numbered location.
            </p>
          </div>
          
          <VenueMap
            selectedHotelId={selectedHotelId}
            onSelectHotel={handleSelectHotel}
          />
          
          <div className="flex flex-wrap justify-center gap-6 md:gap-8 mt-8 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 bg-[#B78569] border-2 border-forest rounded-full"></span>
              <span className="text-forest/70">Wedding Venue</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 bg-ivory border-2 border-rust rounded-full"></span>
              <span className="text-forest/70">Friday · Barcocina</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 bg-stone border-2 border-olive rounded-full"></span>
              <span className="text-forest/70">Sagamore Pendry</span>
            </div>
            <div className="flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-3.5 w-7 rounded-full border-2 border-sage bg-mist"></span>
              <span className="text-forest/70">Hotel Neighborhoods</span>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="section-divider" />
      </div>

      {/* MIDDLE: Featured Hotel - Reduced scale */}
      <section className="py-12 md:py-14 bg-stone">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-5 gap-8 md:gap-12 items-center">
            {/* Image - Smaller */}
            <a
              href={PENDRY_ROOM_BLOCK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View the optional room block at Sagamore Pendry Baltimore"
              className="md:col-span-2 image-hover block"
            >
              <div className="hero-image aspect-[4/3]">
                <img 
                  src={HOTEL_IMAGE}
                  srcSet={HOTEL_IMAGE_SRC_SET}
                  sizes="(min-width: 768px) 40vw, 100vw"
                  alt="Sagamore Pendry Baltimore"
                  data-testid="featured-hotel-image"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </a>
            
            {/* Content */}
            <div className="md:col-span-3">
              <p className="text-xs uppercase tracking-[0.25em] text-olive mb-3">Where We&rsquo;re Staying</p>
              <h2 
                data-testid="featured-hotel-name"
                className="font-giaza text-4xl md:text-5xl text-forest mb-4"
              >
                Sagamore Pendry Baltimore
              </h2>
              
              <p className="text-sm uppercase tracking-[0.12em] text-forest/80 mb-4">
                Fells Point, Baltimore
              </p>
              
              <div className="p-4 bg-forest/5 border-l-2 border-forest">
                <p className="text-lg text-forest leading-relaxed">
                  We&rsquo;ll be calling the Sagamore Pendry home for the weekend,
                  and we&rsquo;d love to have you nearby! We&rsquo;ve set aside a room
                  block if you&rsquo;d like to join us there.
                </p>
              </div>
              <a
                href={PENDRY_ROOM_BLOCK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pendry-booking-link"
                className="btn-outline inline-flex mt-6"
              >
                View Pendry room block
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="section-divider" />
      </div>

      {/* BOTTOM: Recommended Accommodations */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Left: Content */}
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.25em] text-olive mb-4">More Options</p>
              <h2 className="travel-accommodations-title font-giaza text-[2.7rem] leading-[0.95] sm:text-5xl md:text-6xl text-forest mb-6 break-words">
                Recommended<br className="sm:hidden" /> Accommodations
              </h2>
              <p className="text-lg text-forest/70 leading-relaxed mb-8">
                Baltimore offers a variety of excellent hotels for every preference. 
                We recommend booking early to secure your preferred accommodation.
              </p>
              
              {/* Hotel List */}
              <ul data-testid="hotel-list" className="hotel-list">
                {RECOMMENDED_HOTELS.map((hotel, index) => {
                  const isSelected = hotel.id === selectedHotelId;

                  return (
                  <li 
                    key={hotel.id}
                    className={`hotel-list__row ${
                      isSelected
                        ? 'hotel-list__row--selected'
                        : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelectHotel(hotel.id)}
                      aria-pressed={isSelected}
                      aria-label={`Show ${hotel.name} on the map`}
                      data-testid={`hotel-map-button-${index}`}
                      className="hotel-list__button focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust"
                    >
                      <span className={`hotel-list__number ${isSelected ? 'font-bold text-rust' : 'text-olive'}`}>
                        {String(hotel.number).padStart(2, '0')}
                      </span>
                      <span className={`hotel-list__name ${isSelected ? 'text-rust' : 'text-forest'}`}>
                        {hotel.name}
                      </span>
                      <span className="hotel-list__meta">
                        <span className="hotel-list__location text-xs uppercase tracking-wider">{hotel.location}</span>
                        <span aria-hidden="true" className="hotel-list__meta-divider">·</span>
                        <span className="hotel-list__distance text-xs tabular-nums whitespace-nowrap">{hotel.distance}</span>
                      </span>
                    </button>
                    <a
                        href={hotel.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`hotel-link-${index}`}
                        aria-label={`Visit ${hotel.name} website`}
                        className="hotel-list__link focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rust"
                      >
                        Website ↗
                      </a>
                  </li>
                  );
                })}
              </ul>
              <p className="sr-only" aria-live="polite">
                {selectedHotelId
                  ? `${RECOMMENDED_HOTELS.find((hotel) => hotel.id === selectedHotelId)?.name} highlighted on the map.`
                  : ''}
              </p>
            </div>
            
            {/* Right: Library Interior Image */}
            <div className="hidden lg:block">
              <div className="hero-image aspect-[3/4] image-hover sticky top-32">
                <img 
                  src={LIBRARY_INTERIOR}
                  srcSet={LIBRARY_INTERIOR_SRC_SET}
                  sizes="50vw"
                  alt="George Peabody Library Interior"
                  data-testid="library-interior-image"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting to Baltimore */}
      <section className="py-14 md:py-16 bg-mist">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-giaza text-4xl md:text-5xl text-forest mb-6">Getting to Baltimore</h3>
          <p className="text-lg text-forest/70 leading-relaxed">
            Baltimore/Washington International Airport is approximately 20&ndash;30 minutes from central Baltimore by car. Baltimore Penn Station offers Amtrak and MARC service and is approximately 10 minutes from the George Peabody Library. Rideshare and taxi services are readily available throughout Baltimore and are recommended for transportation during the wedding weekend.
          </p>
        </div>
      </section>
    </div>
  );
}
